/** * 報告書詳細表示コンポーネント */
<template>
  <ifield-b-modal
    id="the-report-detail"
    ref="the-report-detail-modal"
    v-bind:title="$t('theReportDetail.report')"
    size="xl"
    centered
    no-body-border
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hidden="$_onHideModal"
  >
    <b-row v-if="isContentCreated" class="dialog-body">
      <b-col cols="6" class="dialog-body-left">
        <loading-mask
          v-bind:isFullScreen="isFullScreenLoading"
          v-bind:isLoading="isLoading"
        >
          <!-- 報告者 -->
          <div class="dialog-user-card">
            <card-user
              v-if="data"
              can-user-name-click
              v-bind:user-id="data.report_user_id"
              v-bind:user-name="data.report_user_name"
              v-bind:profile-image-attach-file-type="
                data.report_user_profile_image
                  ? data.report_user_profile_image.attach_file_type
                  : null
              "
              v-bind:update-date="createDate"
              v-bind:groups="groups"
              v-on:usercard-username-click="
                $store.dispatch('updateProfileUserId', $event)
              "
            ></card-user>
          </div>
          <div class="dialog-info-card">
            <!-- 報告書基本情報 -->
            <div class="dialog-info-title">
              <h3 class="title">
                {{ reportTitle }}
              </h3>
              <div class="update-at-text">
                <span class="date">{{ reportUpdateDate }}</span
                ><span class="diff">{{ reportUpdateDiffText }}</span>
              </div>
            </div>
            <p class="dialog-info-comment">
              {{ reportComment }}
            </p>
            <!-- データリストテーブル -->
            <div class="data-list-table-wrapper">
              <data-list-table
                v-bind:class="{ 'data-list-bottom-li': hasAttachFile }"
                v-bind:id="'report-data-list-table'"
                v-bind:data="detailDataList"
              >
                <template v-slot:file-column="{ item }">
                  <attach-file-viewer
                    v-bind:attachFiles="item.value"
                    v-bind:recordId="reportId"
                    v-bind:isAttribute="item.isAttribute"
                    class="viewer"
                  />
                </template>
              </data-list-table>
              <!-- 添付ファイル表示テーブル -->
              <data-list-table
                v-if="hasAttachFile"
                class="attach-file-data-list-table"
                v-bind:id="'attach-file-data-list-table'"
                v-bind:data="attachFileData"
              >
                <template v-slot:attach-file="{ item }">
                  <attach-file-viewer
                    v-bind:attachFiles="item.value"
                    v-bind:recordId="reportId"
                    class="viewer"
                  />
                </template>
              </data-list-table>
            </div>
            <p class="dialog-info-last-update-info">
              {{ lastUpdateInfo }}
            </p>
          </div>
          <!-- 承認 -->
          <div
            class="dialog-info-card-action"
            v-bind:class="{
              'dialog-info-card-action-unfixed': !isCheckedReport,
              'dialog-info-card-action-fixed': isCheckedReport,
            }"
          >
            <p class="message">{{ checkedMessage }}</p>
            <b-button
              v-if="canCheckReport"
              size="sm"
              variant="secondary"
              v-t="'theReportDetail.action.checkReport'"
              v-on:click="$_updateCheck(true)"
            />
            <b-button
              v-if="canUnCheckReport"
              size="sm"
              variant="light"
              v-t="'theReportDetail.action.cancelCheck'"
              v-on:click="$_updateCheck(false)"
            />
          </div>
        </loading-mask>
      </b-col>
      <b-col cols="6" class="dialog-body-right">
        <!-- 地図 -->
        <map-control
          ref="map-control"
          v-bind="$store.getters.mapControlBinds"
          v-bind:id-prefix="mapIdPrefix"
          v-bind:height="'520px'"
          v-bind:center="center"
          v-bind:zoom="18"
          v-bind:store-sync="['mapType']"
          v-bind:control="{ header: false, layerMenu: false }"
          v-bind:can-change-indoor-level="false"
          v-bind:layer-renderers="renderers"
          v-bind:reactive-render="isModalShown"
          v-on:map-control-initend="$_onMapInitEnd"
        />
      </b-col>
    </b-row>
    <!-- 編集ダイアログ -->
    <report-editor
      v-if="isContentCreated"
      ref="report-editor"
      v-on:report-editend="$_onReportEditEnd"
    />

    <!-- modal footer -->
    <template v-slot:modal-footer="{ cancel }">
      <!-- 編集ボタン -->
      <b-button
        v-show="isEditableReport"
        size="sm"
        variant="success"
        v-t="'theReportDetail.action.edit'"
        v-on:click="$_showEditDialog"
      />
      <!-- 印刷ボタン 実装ができていないためコメントアウトする
      <b-button size="sm" variant="success" v-t="'theReportDetail.action.print'" />
      -->
      <!-- EXCEL出力ボタン -->
      <b-button
        size="sm"
        variant="success"
        v-t="'theReportDetail.action.outputExcel'"
        v-on:click="$_outputExcel"
      />
      <!-- 閉じるボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'theReportDetail.action.close'"
        v-on:click="cancel"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import AttachFileViewer from '@/components/AppAttachFileViewer.vue'
import CardUser from '@/components/AppCardUser.vue'
import ReportEditor from '@/views/TheReportEditor.vue'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { DataListTable } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { useAttributeFormatter } from 'ifield-core-components'
import DomainRenderer from '@/js/map-renderer/DomainRenderer.js'
import { inject, ref, computed } from 'vue'
import { mapState } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useDateDisplayText } from '@/composables/UseDateDisplayText'
import {
  toLocalTz,
  toLocalTzAttribute,
  getLocalTimezone,
} from '@/js/TimezoneHelper'
import {
  FUNCTION_TYPE,
  DATE_FORMAT_SLASH,
  TIME_FORMAT_MINUTE,
} from '@/constants.js'

/**
 * 表示用の日時フォーマット
 * @type {string}
 */
const DATETIME_DISPLAY_FORMAT = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_MINUTE}`

// レイヤーID
const LAYER_ID = 'report'

// レンダラーファクトリ
const rendererFactory = new DomainRenderer({ layerId: LAYER_ID })

export default {
  name: 'TheReportDetail',
  components: {
    'data-list-table': DataListTable,
    'card-user': CardUser,
    'report-editor': ReportEditor,
    'attach-file-viewer': AttachFileViewer,
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    const { formatGeom } = useAttributeFormatter()

    /**
     * レポート詳細データ用ref
     * @type {Object|Null}
     */
    const data = ref(null)

    /**
     * レポート更新日時
     * @type {String|Null}
     */
    const _reportUpdateAt = computed(() => data.value?.report_update_at || null)

    // 基準日時
    const baseDate = inject('baseDate')

    // 表示用レポート更新日時、差分期間文字列
    const { formattedDate, diffText } = useDateDisplayText({
      date: _reportUpdateAt,
      baseDate,
    })

    return {
      handleApiError,
      formatGeom,
      baseDate,
      reportUpdateDate: formattedDate,
      reportUpdateDiffText: diffText,
      data,
    }
  },
  data: function () {
    return {
      /**
       * 表示対象のレポートID
       * @type {Number}
       */
      reportId: null,
      /**
       * 報告書データを更新した場合はtrue
       * @type {Boolean}
       */
      isUpdated: false,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 全画面ロード中の場合はtrue
       * @type {Boolean}
       */
      isFullScreenLoading: false,
      /**
       * 自レイヤーのレンダラー
       * @type {Object}
       */
      myRenderer: null,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `report-detail-${this._uid}-`
    },
    ...mapState('reportMarker', ['iconAtlas', 'iconMapping']),
    /**
     * 報告ユーザーの所属グループ
     * @type {Array.<Object>}
     */
    groups: function () {
      if (!this.data) return []

      return (
        this.$store.getters['user/getUserById'](this.data.report_user_id)
          ?.groups || []
      )
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      const renderers = [...this.$store.getters['map/layerRenderers']]
      if (this.myRenderer) {
        renderers.push(this.myRenderer.render)
      }
      return renderers
    },
    /**
     * 中心座標
     * @type {Object}
     */
    center: function () {
      if (
        this.data &&
        this.data.geom &&
        this.data.geom.geometry &&
        this.data.geom.geometry.coordinates
      ) {
        const [lng, lat] = this.data.geom.geometry.coordinates
        return { lat, lng }
      } else {
        return this.$store.getters['map/center']
      }
    },
    /**
     * 作成日時
     * @type {Date}
     */
    createDate: function () {
      return this.data?.report_create_at
        ? new Date(this.data.report_create_at)
        : null
    },
    /**
     * 位置確定状態のメッセージ
     * @return {string}
     */
    checkedMessage: function () {
      if (!this.data) {
        return ''
      }

      if (this.data.checked_flg) {
        let checkedDate = this.data.checked_at
          ? moment(this.data.checked_at).format(DATETIME_DISPLAY_FORMAT)
          : 'Unknown date'
        let userName = this.data.checked_user_name
          ? this.data.checked_user_name
          : 'Unknown user'
        return this.$t('theReportDetail.message.checked', {
          datetime: checkedDate,
          user: userName,
        })
      }
      return this.$t('theReportDetail.message.unChecked')
    },

    /**
     * 報告書が承認ずみの場合はtrue
     * @return {boolean}
     */
    isCheckedReport: function () {
      if (!this.data) {
        return false
      }
      return this.data.checked_flg
    },

    /**
     * 報告書が承認可能な場合はtrue
     * @return {boolean}
     */
    canCheckReport: function () {
      // 承認済みの報告書は承認不可
      if (this.isCheckedReport) {
        return false
      }

      // 未承認の報告書は管理者のみが承認可能
      return this.$store.state.login.isManager
    },

    /**
     * 報告書の承認を解除することが可能な場合はtrue
     * @return {boolean}
     */
    canUnCheckReport: function () {
      if (!this.isCheckedReport) {
        return false
      }

      // 承認済みの報告書は管理者のみが未承認に戻すことが可能
      return this.$store.state.login.isManager
    },

    /**
     * 報告書が編集可能な場合はtrue
     * @return {boolean}
     */
    isEditableReport: function () {
      // 報告書が存在しない場合は編集不可
      if (!this.data) {
        return false
      }

      let isEditable
      if (this.isCheckedReport) {
        // 承認済みの報告書は編集不可
        isEditable = false
      } else {
        // 未承認の報告書の場合、ユーザーの権限で編集可否が決定する
        if (this.$store.state.login.isManager) {
          // 管理者の場合は全ての未承認報告書を編集可能
          isEditable = true
        } else {
          // 管理者以外は、自分の報告書のみ編集可能
          isEditable =
            this.$store.state.login.userId === this.data.report_user_id
        }
      }
      return isEditable
    },

    /**
     * @return {string} 報告書のタイトル
     */
    reportTitle: function () {
      if (this.data) {
        return this.data.title
      } else {
        return ''
      }
    },

    /**
     * @return {string} 報告書のコメント
     */
    reportComment: function () {
      if (this.data) {
        return this.data.comment
      } else {
        return ''
      }
    },

    /**
     * 報告書詳細データからデータリストコンポーネント用のデータを作成
     * @return {Array.<Object>} 報告書詳細表示用データ
     */
    detailDataList: function () {
      if (!this.data) return null

      // 標準項目
      const list = [
        // プロジェクト
        {
          label: this.$t('theReportDetail.field.project'),
          value: this.data.report_project_name,
        },
      ]
      if (this.data.place_name) {
        list.push(
          // プレイス
          {
            label: this.$t('theReportDetail.field.place'),
            value: this.data.place_name,
          }
        )
      } else {
        list.push(
          // 報告位置
          {
            label: this.$t('theReportDetail.field.reportingPosition'),
            value: this.data.geom,
            formatter: this.formatGeom,
          }
        )
      }

      // カスタマイズ属性
      if (this.data.report_columns) {
        for (let attr of this.data.report_columns) {
          if (+attr.attribute_type === this.ATTRIBUTE_TYPE.FILE) {
            // ファイルカラム
            list.push({
              name: 'file-column',
              label: attr.attribute_name,
              type: ATTRIBUTE_TYPE.SLOT,
              isAttribute: true,
              value: attr.attribute_value
                ? attr.attribute_value.split('|').map((fileId) => ({
                    id: fileId,
                    attach_file_type: FUNCTION_TYPE.REPORT,
                  }))
                : [],
            })
          } else {
            list.push({
              label: attr.attribute_name,
              type: attr.attribute_type,
              value: attr.attribute_value,
            })
          }
        }
      }
      return list
    },

    /**
     * 添付ファイル表示コンポーネント用のデータを作成
     * @return {Array.<Object>} 添付ファイル表示用データ
     */
    attachFileData: function () {
      const data = []
      if (this.data && this.data.report_attach_files.length) {
        data.push({
          name: 'attach-file',
          label: this.$t('theReportDetail.field.reportAttachFiles'),
          type: ATTRIBUTE_TYPE.SLOT,
          value: this.data.report_attach_files,
        })
      }
      return data
    },

    /**
     * 添付ファイルの有無判定
     * @return {Boolean} 添付ファイルが存在する場合はtrue
     */
    hasAttachFile: function () {
      return !!this.attachFileData.length
    },

    /**
     * カスタム属性タイプ定数
     * @type {Object}
     */
    ATTRIBUTE_TYPE: function () {
      return ATTRIBUTE_TYPE
    },

    /**
     * 最終更新情報テキスト
     * @type {String}
     */
    lastUpdateInfo: function () {
      if (
        !this.data ||
        !this.data.report_update_at ||
        !this.data.report_update_user_name
      ) {
        return ''
      }

      return this.$t('theReportDetail.lastUpdateInfo', {
        updateAt: moment(this.data.report_update_at).format(
          DATETIME_DISPLAY_FORMAT
        ),
        userName: this.data.report_update_user_name,
      })
    },
  },
  watch: {
    data: function () {
      this.$_updatePinImage()
        .then(() => this.$store.dispatch('reportMarker/updateTexture'))
        .then(() => this.$_updateReportLayer())
    },
  },
  mounted: function () {
    this.$log.info('reportDetail mounted')
  },
  methods: {
    /**
     * モーダルダイアログを表示する
     * @param {Object} reportId 報告書詳細データ
     */
    showModal: function (reportId) {
      this.reportId = reportId
      this.$_showReport()
      this.$refs['the-report-detail-modal'].show()
    },

    /**
     * モーダルダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['the-report-detail-modal'].hide()
    },

    /**
     * モーダルダイアログを表示する時の処理
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },

    /**
     * モーダルダイアログを非表示にする時の処理
     */
    $_onHideModal: function () {
      this.isModalShown = false
      this.$emit('hide-modal', this.isUpdated)
      // クリア処理
      this.$_clear()
    },

    /**
     * クリア処理
     */
    $_clear: function () {
      this.data = null
      this.reportId = null
      this.isUpdated = false
    },

    /**
     * マップ初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
    },

    /**
     * 報告書情報を表示する
     */
    $_showReport: async function () {
      if (!this.reportId) {
        return
      }

      this.$_showLoading()
      // URL作成
      const url = RestApiClient.createApiUrl(`report/${this.reportId}`)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.data = {
          ...d,
          checked_at: toLocalTz(d.checked_at),
          report_create_at: toLocalTz(d.report_create_at),
          report_update_at: toLocalTz(d.report_update_at),
          report_columns: d.report_columns?.map((attr) =>
            toLocalTzAttribute(attr)
          ),
        }
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * 地図上に表示するピン画像データを更新
     */
    $_updatePinImage: async function () {
      if (!this.data) return

      const id = this.data.report_project_id
      try {
        await this.$store.dispatch('reportMarker/getImageById', { id })
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn(
            `failed to update pin image of report project ID ${id}.`
          )
        )
      }
    },

    /**
     * 報告書レイヤー更新
     */
    $_updateReportLayer: function () {
      // データが空のときはレイヤーを作成しない
      if (!this.data) {
        this.myRenderer = null
        return
      }

      const props = {
        iconAtlas: this.iconAtlas,
        iconMapping: this.iconMapping,
        getIcon: (d) => {
          if (this.iconMapping[`${d.properties.report_project_id}`]) {
            return `${d.properties.report_project_id}`
          }
          return 'default'
        },
        updateTriggers: {
          getIcon: [this.iconMapping],
        },
      }
      this.myRenderer = rendererFactory.createRenderer([this.data], props)
    },

    /**
     * 報告書を承認状態を更新する
     * @param {boolean} isChecked 承認する場合はtrue, 承認を解除する場合はfalse
     */
    $_updateCheck: function (isChecked) {
      // URL
      const url = RestApiClient.createApiUrl('report/checked/' + this.data.id)
      // Body
      const body = {
        checked_flg: isChecked,
      }

      RestApiClient.executePutApi(url, body)
        .then(() => {
          this.isUpdated = true
          // 報告書情報を再取得する
          this.$_showReport()
        })
        .catch((error) => {
          this.handleApiError(error)
        })
    },

    /**
     * 報告書編集ダイアログを表示する
     */
    $_showEditDialog: function () {
      if (!this.data) {
        return
      }
      this.$refs['report-editor'].showModal(this.data.id)
    },

    /**
     * 報告書編集が完了したときの処理
     */
    $_onReportEditEnd: function () {
      this.isUpdated = true
      this.$_showReport()
      /**
       * 報告書詳細モーダル内の編集完了後のイベント
       */
      this.$emit('report-detail-editend')
    },

    /**
     * ローディングを表示する
     * @param {Boolean=} [isFullScreen=false] フルスクリーンか？
     */
    $_showLoading: function (isFullScreen = false) {
      this.isFullScreenLoading = isFullScreen
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
      this.isFullScreenLoading = false
    },

    /**
     * 報告書のExcel出力処理
     */
    $_outputExcel: async function () {
      // 全画面ローディングの表示
      this.$_showLoading(true)

      // URL
      const url = RestApiClient.createApiUrl('report/excel/' + this.data.id)
      url.searchParams.append('csv_tz', getLocalTimezone())

      // Excel出力 API 実行
      let response
      try {
        response = await RestApiClient.executeGetApiInResponse(url)
      } catch (e) {
        this.handleApiError(e)
        // ローディングを消す
        this.$_hideLoading()
        return
      }

      const blob = await response.blob()
      const anchor = document.createElement('a')
      anchor.href = window.URL.createObjectURL(blob)
      anchor.download = this.$_getDownloadFileNameFromResponse(response)
      anchor.click()

      // ローディングを消す
      this.$_hideLoading()
    },
    /**
     * レスポンスヘッダからダウンロードファイル名を取得する
     *
     * @param {Response} response レスポンスヘッダ
     * @return {String} ダウンロードファイル名
     */
    $_getDownloadFileNameFromResponse: function (response) {
      const headers = response.headers
      const contentDisposition = headers.get('Content-Disposition')
      if (_.isNil(contentDisposition)) return null
      // マルチバイトの方(UTF-8指定のある方)から取得する
      const fileName = contentDisposition.substring(
        contentDisposition.indexOf("''") + 2,
        contentDisposition.length
      )
      return decodeURI(fileName)
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-body {
  background-color: #ffffff;
  height: 520px;
  margin: 0;
  width: 100%;
}

.dialog-body-left {
  padding-left: 0;
  height: 100%;
}

.dialog-user-card {
  width: 100%;
  height: auto;
  padding: 10px;
  box-sizing: border-box;
  background-color: $entity-color-white;
  border: 1px solid #f0f0f0;
}

.dialog-info-card {
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;
  height: calc(100% - 113px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  text-align: left;
  @include mixin-scrollbar;

  ::v-deep .dialog-info-card {
    padding: 0;
  }
}

.dialog-info-title {
  margin: 0 0 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 16px;
  .title {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    margin: 0;
    overflow-wrap: anywhere;
  }
  .update-at-text {
    font-size: $font-size-small;
    color: #959595;
    text-align: right;
    // 報告書タイトルと上下中央で揃えるため
    padding-top: 1px;

    // 日時表記は日付と時刻の間では折り返さない
    .date {
      white-space: nowrap;
      display: inline-block;
    }
    .diff {
      white-space: nowrap;
      display: inline-block;
      margin-left: 4px;
    }
  }
}

.dialog-info-last-update-info {
  font-size: $font-size-small;
  color: #212529;
  margin: 0;
  border-top: 1px solid #cccccc;
  padding-top: 20px;
}

.dialog-info-comment {
  font-size: $font-size-middle;
  margin: 0;
  white-space: pre-line;
}

.data-list-table-wrapper {
  margin: 16px 0 20px 0;
  .viewer {
    margin: 8px;
  }
}

.data-list-bottom-li {
  ::v-deep .data-list-li:last-child {
    border-bottom: none;
  }
}

.attach-file-data-list-table {
  ::v-deep .data-list-li:first-child {
    border-top: 1px solid #a0a0a0;
  }
}

.dialog-info-card-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 51px;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid #f0f0f0;

  &.dialog-info-card-action-unfixed {
    background-color: $entity-color-white;
  }
  &.dialog-info-card-action-fixed {
    background-color: #6e757c;
    color: #fff;
  }

  .message {
    margin: 0;
    font-size: $font-size-middle;
    word-break: normal;
  }
}

.dialog-body-right {
  padding-right: 0;
}
</style>
